import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, DollarSign, Users, CheckCircle2, X } from 'lucide-react';

const StatsBar = ({ stats, formatCurrency, formatCompactCurrency, getCurrencySymbol }) => {
  const [showVolumeModal, setShowVolumeModal] = useState(false);

  // Build sorted currency breakdown
  const currencyEntries = useMemo(() => {
    const vbc = stats.volumeByCurrency || {};
    return Object.entries(vbc)
      .map(([code, amount]) => ({ code, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [stats.volumeByCurrency]);

  // Show top 2 currencies inline as preview
  const previewEntries = currencyEntries.slice(0, 2);
  const hasMore = currencyEntries.length > 2;

  return (
    <>
      <div className="stats-bar">
        <div className="stat-card transactions">
          <div className="stat-icon-box">
            <BarChart3 />
          </div>
          <div className="stat-info">
            <div className="stat-label">Total Transactions</div>
            <div className="stat-value">{stats.totalCount.toLocaleString()}</div>
          </div>
        </div>

        {/* Total Volume — multi-currency breakdown */}
        <div className="stat-card volume" style={{ cursor: 'pointer' }} onClick={() => currencyEntries.length > 1 && setShowVolumeModal(true)}>
          <div className="stat-icon-box">
            <DollarSign />
          </div>
          <div className="stat-info">
            <div className="stat-label">Total Volume {currencyEntries.length > 1 ? `(${currencyEntries.length} currencies)` : ''}</div>
            {currencyEntries.length === 0 ? (
              <div className="stat-value">$0</div>
            ) : currencyEntries.length === 1 ? (
              <div className="stat-value">{formatCompactCurrency(currencyEntries[0].amount, currencyEntries[0].code)} {currencyEntries[0].code}</div>
            ) : (
              <div className="stat-value" style={{ display: 'flex', flexDirection: 'column', gap: 0, lineHeight: 1.25 }}>
                {previewEntries.map(e => (
                  <span key={e.code} style={{ fontSize: '0.45em', fontWeight: 700 }}>
                    {formatCompactCurrency(e.amount, e.code)}{' '}
                    <span style={{ fontSize: '0.85em', fontWeight: 600, opacity: 0.7 }}>{e.code}</span>
                  </span>
                ))}
                {hasMore && <span style={{ fontSize: '0.36em', opacity: 0.5, fontWeight: 600 }}>+{currencyEntries.length - 2} more…</span>}
              </div>
            )}
          </div>
        </div>

        <div className="stat-card users">
          <div className="stat-icon-box">
            <Users />
          </div>
          <div className="stat-info">
            <div className="stat-label">Unique Users</div>
            <div className="stat-value">{stats.uniqueUsers.toLocaleString()}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon-box">
            <CheckCircle2 />
          </div>
          <div className="stat-info">
            <div className="stat-label">Success Rate</div>
            <div className="stat-value">{stats.successRate}%</div>
          </div>
        </div>
      </div>

      {/* Volume Breakdown Modal */}
      {showVolumeModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowVolumeModal(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: 24, minWidth: 380, maxWidth: 480,
            maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Volume by Currency</h3>
              <button onClick={() => setShowVolumeModal(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6
              }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {currencyEntries.map(({ code, amount }) => (
                <div key={code} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                  border: '1px solid #e2e8f0'
                }}>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#475569' }}>{code}</span>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    {formatCurrency(amount, code)}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8' }}>
              {currencyEntries.length} currencies across {stats.totalCount} transactions
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default StatsBar;
